import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { MONTH_NAMES, DOW_NAMES } from "@/lib/i18n";
import { toISO, todayISO, buildMonthGrid } from "@/lib/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import CalendarEventDetail from "@/components/admin/CalendarEventDetail";
import { MOBILE_STATUSES, formatAdminDate } from "@/components/admin/adminShared";

const FILTERS = [{ k: "all", l: "Tot" }, { k: "venue", l: "Sala" }, { k: "mobile", l: "Externs" }];

const chipClass = (it) => {
  if (it.source === "mobile") return MOBILE_STATUSES[it.status]?.chip || "";
  return it.type === "public"
    ? "bg-gradient-to-br from-[#E2C9A0] to-[#C9A24B] text-[#2e5949] border border-[#C9A24B]"
    : "bg-[#450905] text-[#fdfcf9] border border-[#450905]";
};

const LEGEND = [
  { l: "Sala · públic", c: "bg-gradient-to-br from-[#E2C9A0] to-[#C9A24B]" },
  { l: "Sala · privat", c: "bg-[#450905]" },
  ...Object.values(MOBILE_STATUSES).map((s) => ({ l: `Extern · ${s.label}`, c: s.chip })),
];

function Stat({ label, value, testid }) {
  return (
    <div className="bg-white border border-[#eae0df] rounded-2xl px-5 py-4">
      <p className="text-[10px] uppercase tracking-[0.22em] text-[#5C544F]">{label}</p>
      <p className="font-display text-3xl mt-1" data-testid={testid}>{value}</p>
    </div>
  );
}

export default function AdminMasterCalendar({ onEdit }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [showCancelled, setShowCancelled] = useState(false);
  const [selected, setSelected] = useState(null);
  const [dayList, setDayList] = useState(null);

  useEffect(() => {
    let active = true;
    api.get("/admin/calendar", { params: { year, month: month + 1 } })
      .then((r) => { if (active) setItems(r.data); })
      .catch(() => { if (active) toast.error("Error carregant el calendari."); });
    return () => { active = false; };
  }, [year, month]);

  const visible = useMemo(() => items.filter((i) =>
    (filter === "all" || i.source === filter) && (showCancelled || i.status !== "cancelled")), [items, filter, showCancelled]);
  const byDate = useMemo(() => visible.reduce((acc, i) => ({ ...acc, [i.date]: [...(acc[i.date] || []), i] }), {}), [visible]);
  const today = todayISO();
  const go = (d) => { const n = new Date(year, month + d, 1); setYear(n.getFullYear()); setMonth(n.getMonth()); };

  return (
    <div data-testid="admin-master-calendar">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="uppercase tracking-[0.28em] text-xs text-[#3e664c] mb-2">Intern</p>
          <h2 className="font-display text-4xl tracking-tight">Calendari d&apos;esdeveniments</h2>
        </div>
        <div className="flex items-center gap-1 bg-white border border-[#eae0df] rounded-full p-1" data-testid="master-filter">
          {FILTERS.map((f) => (
            <button key={f.k} onClick={() => setFilter(f.k)} data-testid={`master-filter-${f.k}`}
              className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-[0.16em] transition-colors ${filter === f.k ? "bg-[#3e664c] text-white" : "text-[#5C544F] hover:text-[#3e664c]"}`}>
              {f.l}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat label="Sala" value={items.filter((i) => i.source === "venue").length} testid="master-stat-venue" />
        <Stat label="Externs confirmats" value={items.filter((i) => i.source === "mobile" && i.status === "confirmed").length} testid="master-stat-mobile-confirmed" />
        <Stat label="Externs pendents" value={items.filter((i) => i.source === "mobile" && i.status === "pending").length} testid="master-stat-mobile-pending" />
        <Stat label="Total visibles" value={visible.length} testid="master-stat-visible" />
      </div>

      <div className="bg-white border border-[#eae0df] rounded-2xl p-4 md:p-6">
        <div className="flex items-center justify-between mb-5">
          <button onClick={() => go(-1)} data-testid="master-prev" aria-label="prev" className="w-9 h-9 rounded-full border border-[#eae0df] flex items-center justify-center hover:border-[#3e664c]"><ChevronLeft size={16} /></button>
          <h3 className="font-display text-3xl" data-testid="master-month">{MONTH_NAMES.ca[month]} <span className="text-[#3e664c]">{year}</span></h3>
          <button onClick={() => go(1)} data-testid="master-next" aria-label="next" className="w-9 h-9 rounded-full border border-[#eae0df] flex items-center justify-center hover:border-[#3e664c]"><ChevronRight size={16} /></button>
        </div>
        <div className="grid grid-cols-7 gap-1 mb-1">
          {DOW_NAMES.ca.map((d) => <div key={d} className="text-center text-[10px] uppercase tracking-[0.18em] text-[#5C544F]/70 py-1">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {buildMonthGrid(year, month).map((day, idx) => {
            if (day === null) return <div key={`e-${idx}`} className="min-h-[96px] rounded-lg bg-[#fdfcf9]/50" />;
            const iso = toISO(year, month, day);
            const evs = byDate[iso] || [];
            return (
              <div key={iso} data-testid={`master-day-${iso}`}
                className={`min-h-[96px] rounded-lg border p-1.5 flex flex-col gap-1 ${iso === today ? "border-[#3e664c] bg-[#3e664c]/5" : "border-[#eae0df]"} ${iso < today ? "opacity-60" : ""}`}>
                <span className="text-xs text-[#5C544F] font-medium">{day}</span>
                {evs.slice(0, 3).map((it) => (
                  <button key={`${it.source}-${it.id}`} onClick={() => setSelected(it)} data-testid={`master-chip-${it.source}-${it.id}`} title={it.title}
                    className={`text-left text-[10px] leading-tight px-1.5 py-1 rounded-md truncate hover:opacity-80 transition-opacity ${chipClass(it)}`}>
                    {it.time && <span className="opacity-75 mr-1">{it.time}</span>}{it.source === "mobile" ? "◆ " : ""}{it.title}
                  </button>
                ))}
                {evs.length > 3 && (
                  <button onClick={() => setDayList(iso)} className="text-[10px] text-[#3e664c] text-left px-1" data-testid={`master-more-${iso}`}>+{evs.length - 3} més</button>
                )}
              </div>
            );
          })}
        </div>
        <div className="mt-5 flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap gap-4 text-[10px] uppercase tracking-[0.18em] text-[#5C544F]">
            {LEGEND.map((x) => <span key={x.l} className="flex items-center gap-2"><span className={`w-3 h-3 rounded ${x.c}`} />{x.l}</span>)}
          </div>
          <label className="flex items-center gap-2 text-xs text-[#5C544F] cursor-pointer">
            <Checkbox checked={showCancelled} onCheckedChange={(v) => setShowCancelled(!!v)} data-testid="master-show-cancelled" /> Mostrar cancel·lats
          </label>
        </div>
      </div>

      <Dialog open={!!dayList} onOpenChange={(o) => !o && setDayList(null)}>
        <DialogContent className="bg-white max-w-md" data-testid="master-day-list">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl tracking-tight">{formatAdminDate(dayList)}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-2 max-h-[60vh] overflow-y-auto">
            {(byDate[dayList] || []).map((it) => (
              <button key={`${it.source}-${it.id}`} onClick={() => { setDayList(null); setSelected(it); }}
                data-testid={`master-day-list-item-${it.source}-${it.id}`}
                className={`text-left text-sm px-3 py-2 rounded-lg hover:opacity-85 transition-opacity ${chipClass(it)}`}>
                {it.time && <span className="opacity-75 mr-2">{it.time}</span>}{it.source === "mobile" ? "◆ " : ""}{it.title}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      <CalendarEventDetail item={selected} onClose={() => setSelected(null)} onEdit={(it) => { setSelected(null); onEdit(it); }} />
    </div>
  );
}
